import { useEffect, useState } from 'react';
import formatRFC3339 from 'date-fns/formatRFC3339';
import fromUnixTime from 'date-fns/fromUnixTime';

import countBy from 'lodash/countBy';
import range from 'lodash/range';

import CardHeader from '../common/Labels/CardHeader';
import ChartCard from '../charts/ChartCard/ChartCard';
import { ChartProvider } from '../charts/ChartContext';
import Label from '../common/Labels/Label';
import TimeRangePicker from '../TimeRangePicker/TimeRangePicker';

import {
  getBaselineSamplesFromResponse,
  getRoundedRangeLabel,
} from '../../utils/assay_utils';
import fetchAssayBaseline from '../../utils/api_functions/assayBaselineFetch';

import useClientContext from '../contexts/ClientData/ClientData';
import usePipelineData from '../pipelines/PipelineDataContext';
import usePreferredTimeRange from '../contexts/TimeRangeContext/TimeRangeContext';
import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

import { useAssayLoadingContext } from './AssayLoadingContext';

const getBaselineChartOptions = iopath => ({
  scales: {
    x: {
      display: true,
      barPercentage: 1.3,
      ticks: {
        autoSkip: false,
        // max: 4,
      },
      title: {
        display: true,
        text: iopath.split(' ').join('_')
      }
    },
    y: {
      ticks: {
        beginAtZero: true,
        callback: val => Math.round(val)
      },
      title: {
        display: true,
        text: 'Count',
      }
    }
  }
})

export const SelectBaselineChart = ({
  setError,
  assayConfig,
  setAssayConfig,
  setNextIsDisabled
}) => {
  const {
    startTimestamp,
    endTimestamp,
    selectedTimeRange,
    onTimeRangeChange,
    binCount = 15,
  } = usePreferredTimeRange()
  const { useArrowLogFormat } = useClientContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { pipeline } = usePipelineData();
  const { setAssayPreviewLoading, assayPreviewLoading } = useAssayLoadingContext();
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);

  // Persist selected baseline range to config
  useEffect(() => {
    const formattedStartDate = formatRFC3339(fromUnixTime(startTimestamp));
    const formattedEndDate = formatRFC3339(fromUnixTime(endTimestamp));

    setAssayConfig(conf => conf.setBaselineRange(formattedStartDate, formattedEndDate));
  }, [startTimestamp, endTimestamp, setAssayConfig])

  const loadAllBaselineLogs = async (pipelineID, workspaceId, start, end) => {
    let baselineLogs = [];
    let next = null;

    let ret = await fetchAssayBaseline({
      pipelineID,
      workspaceId,
      start,
      end,
    });
    let ret_json = await ret.json();
    
    while (ret_json.length > 0) {
      baselineLogs = baselineLogs.concat(ret_json);

      const iteration_status = ret.headers.get('x-iteration-status')
      if (iteration_status) {
        try {
          const parsed_iteration_status = JSON.parse(iteration_status);
          next = parsed_iteration_status?.next;
        } catch (e) {
          break;
        }
      } else if (ret_json?.next) {
        try {
          next = JSON.parse(ret_json?.next);
        } catch (e) {
          break;
        }
      } else {
        break;
      }

      if (!next) {
        break;
      }

      // This await is not parallelizable.
      // eslint-disable-next-line no-await-in-loop
      ret = await fetchAssayBaseline({
        pipelineID,
        workspaceId,
        start,
        end,
        next
      });
      // This await is not parallelizable.
      // eslint-disable-next-line no-await-in-loop
      ret_json = await ret.json();
    }
    return baselineLogs;
  }

  // Generate a chart of historical results to use as a baseline.
  useEffect(() => {
    (async () => {
      const formattedStartDate = formatRFC3339(fromUnixTime(startTimestamp));
      const formattedEndDate = formatRFC3339(fromUnixTime(endTimestamp));

      setError(null);

      try {
        setAssayPreviewLoading(true)
        const ret = await loadAllBaselineLogs(
          pipeline.pipelineID,
          currentWorkspaceId,
          formattedStartDate,
          formattedEndDate,
        );

        if (!ret || ret.length < 1) {
          setAssayPreviewLoading(false)
          // We are explicitly not setting an error here because the chart will show 'not enough data'.
          setNextIsDisabled(true);
          setData([]);
          return;
        }

        // Step 1: Get all outputs from the result data.
        const outputs = getBaselineSamplesFromResponse(ret, assayConfig.getIOPath(), useArrowLogFormat);

        // Step 2: Calculate max/min from the data set.
        const outputsMin = Math.min(...outputs);
        // TODO: Collapse into one reduce for efficiency.
        const outputsMax = Math.max(...outputs);

        // Step 3: Calculate PTP range.
        // TODO: Does ptp calculation need bigmath support?
        const ptp = outputsMax - outputsMin;

        // Step 4: Auto-calculate the bin size according to sqrt algorithm.
        const autoBinWidth = ptp / Math.sqrt(outputs.length);

        // // Step 5: Calculate width of maximum UI-safe 
        const maxNumberOfBins = 50;
        const minimumSafeBinWidth = (outputsMax - outputsMin) / maxNumberOfBins;

        // Step 6: Create x-axis array of bins
        const autoBuckets = range(outputsMin, outputsMax, Math.max(autoBinWidth, minimumSafeBinWidth))

        setLabels(autoBuckets.map(x => getRoundedRangeLabel(x, x + autoBinWidth)));

        // Step 7: Aggregate count over bin range
        const bucketCounts = countBy(outputs, out => autoBuckets.find((x, i) => {
          if (i + 1 >= autoBuckets.length) return x;
          return out >= x && out < autoBuckets[i + 1];
        }))

        const sortedCounts = autoBuckets.map(x => bucketCounts[x]);
        setData(sortedCounts);

        // The number of data points required to run an assay
        if (ret.length < 100) {
          setError('Not enough data is available for an assay.');
          setNextIsDisabled(true);
        } else {
          setNextIsDisabled(false);
        }
      } catch (e) {
        // Intentional error.
        // eslint-disable-next-line no-console
        console.error(e);
        setError('Failed to load baseline.');
      }
      setAssayPreviewLoading(false)
    })();
  }, [assayConfig, useArrowLogFormat, binCount, currentWorkspaceId, endTimestamp, pipeline.pipelineID, setError, setNextIsDisabled, startTimestamp, setAssayPreviewLoading]);

  return <>
    <div className="flex flex-col justify-start items-start mb-4">
      <CardHeader>Specify Baseline</CardHeader>
      <div className="flex flex-col ml-2 my-1">
        <Label className="mb-2" size="sm">
          We&#39;ve selected a baseline (based on historical data) for your assay but you can make adjustments if necessary.
        </Label>
        <TimeRangePicker selectedRange={selectedTimeRange} defaultRange={selectedTimeRange} onChange={onTimeRangeChange} />
      </div>
    </div>
    <ChartProvider
      chartType="bar"
      chartConfiguration="histogram"
      datasets={[{
        data,
        backgroundColor: 'rgba(3, 55, 78, 0.7)'
      }]}
      labels={labels}
      dropdownOptions={[]}
      options={getBaselineChartOptions(assayConfig.getIOPath())}
    >
      <ChartCard
        chartTitle="Baseline"
        chartID={1}
        isLoading={assayPreviewLoading}
        message={data?.length > 0 ? null : 'Not enough data to generate baseline.'}
        loadingText="Generating baseline preview..."
      />
    </ChartProvider>
  </>
}

export default SelectBaselineChart;
